import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import { Search, Eye, AlertCircle as UrgencyLogoIcon } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StandardBadge = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  borderRadius: 6,
  backgroundColor: 'rgba(255, 171, 0, 0.08)',
  color: '#FF9800',
  border: '1px solid rgba(255, 171, 0, 0.24)',
  fontWeight: 700,
  fontSize: '0.9rem',
  lineHeight: 1
}));

export default function Urgency() {
  const navigate = useNavigate();
  const [urgencyList, setUrgencyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchUrgency = async () => {
    try {
      const res = await api.get('/urgency');
      setUrgencyList(res.data);
    } catch (error) {
      console.error('Error fetching urgency list:', error);
      toast.error('Errore nel caricamento dei dati Urgency');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrgency();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalNegotiations = urgencyList.reduce(
    (sum, item) => sum + (item.total_negotiations || 0),
    0
  );

  const filteredList = urgencyList
    .filter((item) => {
      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return (
        item.label.toLowerCase().includes(search) ||
        (item.description && item.description.toLowerCase().includes(search)) ||
        item.id.toString().includes(search) ||
        item.level_id.toString().includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id' || orderBy === 'level_id' || orderBy === 'total_negotiations') {
        const aVal = a[orderBy] || 0;
        const bVal = b[orderBy] || 0;
        return isAsc ? aVal - bVal : bVal - aVal;
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  const goToNegotiations = (item) => {
    const urgencyValue = item.level_id;
    navigate(`/negotiation?urgency=${encodeURIComponent(urgencyValue)}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <UrgencyLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Urgency (Remoto)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Livelli di urgenza utilizzati nel monitoraggio delle pratiche
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per label, descrizione..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'level_id'}
                    direction={orderBy === 'level_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('level_id')}
                  >
                    Level ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'label'}
                    direction={orderBy === 'label' ? order : 'asc'}
                    onClick={() => handleRequestSort('label')}
                  >
                    Label
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'description'}
                    direction={orderBy === 'description' ? order : 'asc'}
                    onClick={() => handleRequestSort('description')}
                  >
                    Descrizione
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_negotiations'}
                    direction={orderBy === 'total_negotiations' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_negotiations')}
                  >
                    Totale Pratiche
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  % sul totale
                </TableCell>
                <TableCell align="center">
                  Dettaglio
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => {
                    const total = item.total_negotiations || 0;
                    const percentage = totalNegotiations > 0
                      ? ((total * 100) / totalNegotiations)
                      : 0;

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>
                          <StandardBadge
                            style={{
                              width: 28,
                              height: 28,
                              minWidth: 28,
                              fontSize: '0.8rem',
                              borderRadius: 4
                            }}
                          >
                            {item.level_id}
                          </StandardBadge>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{item.label}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">
                          <StandardBadge
                            style={{
                              minWidth: 40,
                              height: 40,
                              fontSize: '0.95rem'
                            }}
                          >
                            {total}
                          </StandardBadge>
                        </TableCell>
                        <TableCell align="right">
                          {totalNegotiations > 0 ? (
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={36}
                                thickness={6}
                                sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={percentage}
                                size={36}
                                thickness={6}
                                sx={{ position: 'absolute', left: 0 }}
                                color="warning"
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {percentage.toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => goToNegotiations(item)}>
                            <Eye size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
